import { toast } from "sonner";

import type { BulkUpdateResult } from "@/features/work-management/workTasks.api";

const MAX_NAMED = 5;

/**
 * BULK-HARDEN-1: what a bulk update did NOT touch, in words. Null when every
 * selected task was written. The server skips tasks in a phase that is frozen
 * while its gate is under review; this names how many, which, and why, so a
 * partial result never reads like a complete one.
 */
export function bulkSkippedSummary(
  result: Pick<BulkUpdateResult, "updated" | "skippedCount" | "skipped"> | undefined,
): { title: string; description: string } | null {
  // Fail quiet on a missing result (a caller's mock, an older server): no
  // summary is the same as "nothing was skipped", which is what they mean.
  if (!result) return null;
  const skipped = result.skipped ?? [];
  const count = result.skippedCount ?? skipped.length;
  if (count <= 0) return null;

  const noun = (n: number) => `${n} task${n === 1 ? "" : "s"}`;
  const title = `${noun(result.updated)} updated, ${noun(count)} skipped`;

  const reasons = Array.from(new Set(skipped.map((s) => s.reason).filter(Boolean)));
  const named = skipped
    .slice(0, MAX_NAMED)
    .map((s) => s.taskTitle?.trim() || "Untitled task");
  const more = count > named.length ? ` and ${count - named.length} more` : "";
  const why = reasons.length > 0 ? `${reasons.join("; ")}. ` : "";
  const description = `${why}Skipped: ${named.join(", ")}${more}.`;
  return { title, description };
}

/** Shows the skipped-tasks warning toast. Returns true when it showed one. */
export function notifyBulkSkipped(
  result: Pick<BulkUpdateResult, "updated" | "skippedCount" | "skipped"> | undefined,
): boolean {
  const summary = bulkSkippedSummary(result);
  if (!summary) return false;
  toast.warning(summary.title, {
    description: summary.description,
    duration: 8000,
  });
  return true;
}
